import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
    Box, Paper, Typography, Grid, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, TextField, Button, Chip, Stack, Select, MenuItem, FormControl,
    Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Tooltip, FormControlLabel, Checkbox,
    RadioGroup, Radio, CircularProgress
} from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import EditNoteIcon from '@mui/icons-material/EditNote';
import DescriptionIcon from '@mui/icons-material/Description';
import CalculateIcon from '@mui/icons-material/Calculate';
import PhoneInTalkIcon from '@mui/icons-material/PhoneInTalk';
import { toast } from 'react-toastify';
import { setMainHeading } from '../../../../redux/modules/mainHeading/slice';
import { getLabOrderByIdRequest, resetGetLabOrderById } from '../../../../redux/modules/laboratory/labOrder/slice';
import {
    enterLabResultRequest, resetEnterLabResult,
    getLabResultByOrderRequest, resetGetLabResultByOrder,
    techValidateLabResultRequest, resetTechValidateLabResult,
    batchTechValidateLabResultRequest, resetBatchTechValidateLabResult,
    authorizeLabResultRequest, resetAuthorizeLabResult,
    batchAuthorizeLabResultRequest, resetBatchAuthorizeLabResult,
    amendLabResultRequest, resetAmendLabResult,
    criticalNotifyLabResultRequest, resetCriticalNotifyLabResult,
    confirmContactLabResultRequest, resetConfirmContactLabResult,
    getLabResultTestHistoryRequest, resetGetLabResultTestHistory,
} from '../../../../redux/modules/laboratory/labResult/slice';
import { usePermissions } from '../../../../hooks/usePermissions';

const flagColors = { N: 'success', L: 'warning', H: 'warning', LL: 'error', HH: 'error', A: 'info', AA: 'error' };
const flagLabels = { N: 'Normal', L: 'Low', H: 'High', LL: 'Crit Low', HH: 'Crit High' };
const statusColors = { pending: 'default', preliminary: 'info', tech_validated: 'warning', final: 'success', amended: 'secondary', cancelled: 'error' };

// Format a single reference-range object for display.
const formatRange = (r) => {
    if (!r) return '-';
    if (r.lowNormal != null && r.highNormal != null) return `${r.lowNormal} - ${r.highNormal}`;
    if (r.highNormal != null) return `< ${r.highNormal}`;
    if (r.lowNormal != null) return `> ${r.lowNormal}`;
    if (r.textNormal) return r.textNormal;
    return '-';
};

// Short label for a range: its explicit label, else derived from sex/age band.
const rangeLabel = (r) => {
    if (!r) return '';
    if (r.label) return r.label;
    const parts = [];
    if (r.gender && r.gender !== 'all') parts.push(r.gender === 'male' ? 'Male' : r.gender === 'female' ? 'Female' : r.gender);
    if (r.ageMin != null && r.ageMax != null) parts.push(`${r.ageMin}–${r.ageMax}y`);
    else if (r.ageMin != null) parts.push(`≥${r.ageMin}y`);
    else if (r.ageMax != null) parts.push(`≤${r.ageMax}y`);
    return parts.join(' ') || 'All';
};

// Client-side mirror of the server's findMatchingRange (helpers/labResultHelper),
// used only to preview which range "Auto" resolves to for this patient.
const matchRange = (ranges, age, gender) => {
    if (!ranges || ranges.length === 0) return null;
    const candidates = ranges.filter(r => {
        const genderMatch = !r.gender || r.gender === 'all' || r.gender === gender;
        const ageMatch = (r.ageMin == null || (age != null && age >= r.ageMin))
            && (r.ageMax == null || (age != null && age <= r.ageMax));
        return genderMatch && ageMatch;
    });
    if (candidates.length === 0) return null;
    return candidates.find(r => r.gender === gender) || candidates[0];
};

// GTT — the results-entry page consolidates the GTT panel's per-timepoint
// component tests (GTTFBS/GTT30/…, GTTUFBS/GTTU30/…) into one compact widget.
// They still save as ordinary per-test results (blood → numeric, urine → coded),
// so the save/init logic is unchanged.
const GTT_TP_ORDER = ['FBS', '30', '60', '90', '120'];
const GTT_TP_LABEL = { FBS: 'FBS', '30': '30 min', '60': '60 min', '90': '90 min', '120': '120 min' };
const gttMatch = (code) => (code || '').match(/^GTT(U?)(FBS|\d+)$/);
// Group an order's tests into ordered GTT timepoints (blood + urine per timepoint).
const buildGttGroups = (tests) => {
    const byTp = {};
    (tests || []).forEach((t) => {
        const test = t.testId;
        const m = gttMatch(test?.testCode);
        if (!m || typeof test !== 'object') return;
        const tp = m[2];
        if (!byTp[tp]) byTp[tp] = { tp, label: GTT_TP_LABEL[tp] || tp };
        const entry = { id: String(test._id || t.testId), test };
        if (m[1] === 'U') byTp[tp].urine = entry; else byTp[tp].blood = entry;
    });
    return GTT_TP_ORDER.filter((tp) => byTp[tp]).map((tp) => byTp[tp]);
};
// Non-pregnant/Pregnant range text for a GTT blood test.
const gttRangeStr = (test) => (test?.referenceRanges || []).map((r) => `${rangeLabel(r)}: ${formatRange(r)}`).join('  ·  ');

export default function ResultEntry() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const basePath = location.pathname.split('/laboratory')[0];
    const { id } = useParams();
    const { getByIdRes } = useSelector(state => state.labOrder);
    const { getByOrderRes, loading: resultLoading, enterRes, techValidateRes, batchTechValidateRes, authorizeRes, batchAuthorizeRes, amendRes, criticalNotifyRes, confirmContactRes, getTestHistoryRes } = useSelector(state => state.labResult);
    // The store still holds the previously opened order and its results until
    // this one's arrive. Use only what belongs to the order in the URL, so one
    // patient's results can never fill (and be saved onto) another's form.
    const order = getByIdRes?.data && String(getByIdRes.data._id) === String(id) ? getByIdRes.data : null;
    const results = useMemo(
        () => (getByOrderRes?.data || []).filter((r) => String(r.labOrderId?._id || r.labOrderId) === String(id)),
        [getByOrderRes, id]
    );

    // Capability gates for the result workflow (enter → validate → authorize).
    const { canWrite, can } = usePermissions();
    const canEnter = canWrite('labResults');
    const canValidate = can('labResultValidation:write');
    const canAuthorize = can('labResultAuth:write');

    // Eureka flow flags
    const features = useSelector(state => state.hospitalConfig?.features || {});
    const abnormalContactGate = !!features['lis.abnormalContactGate'];

    const [values, setValues] = useState({});
    // Per-test reference-range selection ('' = Auto age/sex). Sent as
    // selectedRangeId so the server flags against the chosen range.
    const [selectedRanges, setSelectedRanges] = useState({});
    // Two per-test notes: an internal technician comment (lab-only, not printed)
    // and a report note (interpretiveNotes) that prints on the patient report.
    const [notes, setNotes] = useState({});
    const [reportNotes, setReportNotes] = useState({});

    // Abnormal-contact dialog state
    const [contactOpen, setContactOpen] = useState(false);
    const [contactResult, setContactResult] = useState(null);
    const [contactedParty, setContactedParty] = useState('physician');
    const [contactedName, setContactedName] = useState('');
    const [contactNotes, setContactNotes] = useState('');

    // An abnormal result is locked from finalize until contact is recorded
    const isAbnormal = (r) => r?.flag && r.flag !== 'N';
    const contactPending = (r) => abnormalContactGate && isAbnormal(r) && !r?.abnormalContactConfirmedAt;

    // Critical notification dialog state
    const [criticalOpen, setCriticalOpen] = useState(false);
    const [criticalResult, setCriticalResult] = useState(null);
    const [notifiedTo, setNotifiedTo] = useState('');
    const [readBackConfirmed, setReadBackConfirmed] = useState(false);

    // Amend dialog state
    const [amendOpen, setAmendOpen] = useState(false);
    const [amendResult, setAmendResult] = useState(null);
    const [amendValue, setAmendValue] = useState('');
    const [amendReason, setAmendReason] = useState('');

    // Test history dialog state
    const [historyOpen, setHistoryOpen] = useState(false);
    const [historyTestName, setHistoryTestName] = useState('');

    useEffect(() => {
        dispatch(setMainHeading("Result Entry"));
        // A different order: start from an empty form and drop the last one
        // from the store before loading this one.
        setValues({});
        setSelectedRanges({});
        setNotes({});
        setReportNotes({});
        dispatch(resetGetLabOrderById());
        dispatch(resetGetLabResultByOrder());
        if (id) {
            dispatch(getLabOrderByIdRequest(id));
            dispatch(getLabResultByOrderRequest(id));
        }
    }, [dispatch, id]);

    // Initialize form values from existing results
    useEffect(() => {
        if (results.length > 0) {
            const vals = {};
            const ranges = {};
            const nts = {};
            const rnts = {};
            results.forEach(r => {
                const testId = r.testId?._id || r.testId;
                if (r.numericValue !== null && r.numericValue !== undefined) vals[testId] = r.numericValue.toString();
                else if (r.codedValue) vals[testId] = r.codedValue;
                else if (r.textValue) vals[testId] = r.textValue;
                else if (r.multilineValue) vals[testId] = r.multilineValue;
                if (r.selectedRangeId) ranges[testId] = r.selectedRangeId;
                if (r.technicianComment) nts[testId] = r.technicianComment;
                if (r.interpretiveNotes) rnts[testId] = r.interpretiveNotes;
            });
            setValues(vals);
            setSelectedRanges(ranges);
            setNotes(nts);
            setReportNotes(rnts);
        }
    }, [results]);

    // Handle save/action responses
    useEffect(() => {
        if (enterRes) {
            toast(enterRes?.message);
            if (enterRes?.success) dispatch(getLabResultByOrderRequest(id));
            dispatch(resetEnterLabResult());
        }
    }, [enterRes, dispatch, id]);

    useEffect(() => {
        if (techValidateRes) {
            toast(techValidateRes?.message);
            // Refetch on failure too: a failure usually means this view's copy is
            // stale (already validated elsewhere), so re-sync the list to the true
            // server state instead of leaving a stale row that keeps erroring.
            dispatch(getLabResultByOrderRequest(id));
            dispatch(resetTechValidateLabResult());
        }
    }, [techValidateRes, dispatch, id]);

    useEffect(() => {
        if (authorizeRes) {
            toast(authorizeRes?.message);
            if (authorizeRes?.success) dispatch(getLabResultByOrderRequest(id));
            dispatch(resetAuthorizeLabResult());
        }
    }, [authorizeRes, dispatch, id]);

    useEffect(() => {
        if (batchTechValidateRes) {
            toast(batchTechValidateRes?.message);
            if (batchTechValidateRes?.success) dispatch(getLabResultByOrderRequest(id));
            dispatch(resetBatchTechValidateLabResult());
        }
    }, [batchTechValidateRes, dispatch, id]);

    useEffect(() => {
        if (batchAuthorizeRes) {
            toast(batchAuthorizeRes?.message);
            if (batchAuthorizeRes?.success) dispatch(getLabResultByOrderRequest(id));
            dispatch(resetBatchAuthorizeLabResult());
        }
    }, [batchAuthorizeRes, dispatch, id]);

    useEffect(() => {
        if (amendRes) {
            toast(amendRes?.message);
            if (amendRes?.success) { dispatch(getLabResultByOrderRequest(id)); setAmendOpen(false); setAmendReason(''); setAmendValue(''); }
            dispatch(resetAmendLabResult());
        }
    }, [amendRes, dispatch, id]);

    useEffect(() => {
        if (criticalNotifyRes) {
            toast(criticalNotifyRes?.message);
            if (criticalNotifyRes?.success) { dispatch(getLabResultByOrderRequest(id)); setCriticalOpen(false); setNotifiedTo(''); setReadBackConfirmed(false); }
            dispatch(resetCriticalNotifyLabResult());
        }
    }, [criticalNotifyRes, dispatch, id]);

    useEffect(() => {
        if (confirmContactRes) {
            toast(confirmContactRes?.message);
            if (confirmContactRes?.success) { dispatch(getLabResultByOrderRequest(id)); setContactOpen(false); setContactedName(''); setContactNotes(''); }
            dispatch(resetConfirmContactLabResult());
        }
    }, [confirmContactRes, dispatch, id]);

    const handleRecordContact = (result) => {
        setContactResult(result);
        setContactedParty('physician');
        setContactedName(result?.abnormalContactName || order?.orderingPhysicianName || '');
        setContactNotes('');
        setContactOpen(true);
    };
    const confirmRecordContact = () => {
        if (!contactedName.trim()) { toast("Enter who was contacted"); return; }
        dispatch(confirmContactLabResultRequest({
            id: contactResult._id,
            contactedParty,
            contactedName: contactedName.trim(),
            notes: contactNotes.trim() || undefined,
        }));
    };

    // Critical notification handlers
    const handleCriticalNotify = (result) => {
        setCriticalResult(result);
        setNotifiedTo('');
        setReadBackConfirmed(false);
        setCriticalOpen(true);
    };
    const confirmCriticalNotify = () => {
        if (!notifiedTo.trim()) { toast("Enter who was notified"); return; }
        dispatch(criticalNotifyLabResultRequest({ id: criticalResult._id, notifiedTo: notifiedTo.trim() }));
    };

    // Amend handlers
    const handleAmend = (result, test) => {
        setAmendResult({ ...result, test });
        setAmendValue('');
        setAmendReason('');
        setAmendOpen(true);
    };
    const confirmAmend = () => {
        if (!amendReason.trim()) { toast("Reason is required"); return; }
        const payload = { id: amendResult._id, reason: amendReason.trim() };
        const resultType = amendResult.test?.resultType;
        if (resultType === 'numeric' || resultType === 'calculated') {
            // Keep comparator-qualified / non-numeric amendments as text, not NaN.
            if (/[<>≤≥]/.test(amendValue) || isNaN(parseFloat(amendValue))) payload.textValue = amendValue;
            else payload.numericValue = parseFloat(amendValue);
        }
        else if (resultType === 'coded') payload.codedValue = amendValue;
        else if (resultType === 'multiline') payload.multilineValue = amendValue;
        else payload.textValue = amendValue;
        dispatch(amendLabResultRequest(payload));
    };

    // Test history handler
    const handleShowHistory = (testId, testName) => {
        const patientId = order?.patientId?._id;
        if (!patientId) return;
        setHistoryTestName(testName);
        dispatch(getLabResultTestHistoryRequest({ patientId, testId }));
        setHistoryOpen(true);
    };

    const handleSaveResults = () => {
        const resultEntries = [];
        order?.tests?.forEach(t => {
            const testId = t.testId?._id || t.testId;
            const test = t.testId;
            if (t.status === 'cancelled') return; // cancelled tests take no result
            if (test?.resultType === 'calculated') return;
            const val = values[testId];
            if (val === undefined || val === '') return;

            const entry = { labOrderId: id, testId };
            if (test?.resultType === 'numeric') {
                // Same STRICT rule as the server (labResultHelper NUMERIC_RX):
                // a value is numeric only when it is entirely a number.
                // Comparator-qualified ("<2.0", ">1000") and mixed values
                // ("7.2H") are kept as text so they round-trip unchanged — a lax
                // parseFloat here would re-classify a stored text value on the
                // next Save and read as a phantom "change" to a signed-off row.
                if (/^[-+]?(\d+\.?\d*|\.\d+)([eE][-+]?\d+)?$/.test(String(val).trim())) {
                    entry.numericValue = parseFloat(val);
                } else {
                    entry.textValue = val;
                }
                entry.unit = test.unit;
                entry.selectedRangeId = selectedRanges[testId] || null;
            } else if (test?.resultType === 'coded') {
                entry.codedValue = val;
            } else if (test?.resultType === 'multiline') {
                entry.multilineValue = val;
            } else {
                entry.textValue = val;
            }
            // Internal (lab-only) note and report note. Both sent as strings so
            // clearing a note persists; interpretiveNotes prints on the report.
            if (notes[testId] !== undefined) entry.technicianComment = notes[testId].trim();
            if (reportNotes[testId] !== undefined) entry.interpretiveNotes = reportNotes[testId].trim();
            // Find specimen for this test
            const specimen = order.specimens?.find(s => s.testIds?.includes(testId));
            if (specimen) entry.specimenId = specimen._id;
            resultEntries.push(entry);
        });

        if (resultEntries.length === 0) {
            toast("Enter at least one result value");
            return;
        }
        dispatch(enterLabResultRequest({ results: resultEntries }));
    };

    const handleBatchAuthorize = () => {
        // Only tech-validated results may be authorized. A 'preliminary' result
        // must be validated first (single-step profiles enter straight to 'final',
        // so they never appear here as preliminary/tech_validated).
        const eligible = results.filter(r =>
            r.status === 'tech_validated'
            && !contactPending(r)
        );
        const blocked = results.filter(r =>
            ['preliminary', 'tech_validated'].includes(r.status) && !eligible.includes(r)
        );
        if (eligible.length === 0) {
            toast(blocked.length > 0
                ? "All pending results are blocked (awaiting validation signoff or abnormal-result contact)"
                : "No results pending authorization");
            return;
        }
        if (blocked.length > 0) toast(`${blocked.length} result(s) skipped — validation or contact confirmation still required`);
        dispatch(batchAuthorizeLabResultRequest({ resultIds: eligible.map(r => r._id) }));
    };

    const getResultForTest = (testId) => {
        return results.find(r => (r.testId?._id || r.testId) === testId);
    };

    if (!order) {
        return <Typography sx={{ mt: 4 }}>Loading...</Typography>;
    }

    // Patient age/sex for previewing the auto-matched range in the picker.
    const patient = order.patientId;
    const patientGender = patient?.gender || null;
    const patientAge = (() => {
        const dob = patient?.dob;
        if (!dob) return null;
        const d = new Date(dob);
        if (isNaN(d.getTime())) return null;
        return Math.floor((Date.now() - d.getTime()) / (365.25 * 24 * 3600 * 1000));
    })();

    // Cancelled test entries are done — don't ask for results for them. Split the
    // remaining active tests into the GTT widget vs the generic table.
    const activeTests = (order.tests || []).filter((t) => t.status !== 'cancelled');
    const gttGroups = buildGttGroups(activeTests);
    const gttTestIds = new Set(gttGroups.flatMap((g) => [g.blood?.id, g.urine?.id].filter(Boolean)));
    const genericTests = activeTests.filter((t) => !gttTestIds.has(String(t.testId?._id || t.testId)));

    // The GTT widget has no per-row Validate — its timepoints are entered and
    // signed off as one sitting, so one button validates them all.
    const gttPendingValidation = results.filter(r =>
        gttTestIds.has(String(r.testId?._id || r.testId)) && r.status === 'preliminary'
    );
    const handleValidateGtt = () => {
        if (gttPendingValidation.length === 0) return;
        dispatch(batchTechValidateLabResultRequest({ resultIds: gttPendingValidation.map(r => r._id) }));
    };

    return (
        <Box sx={{ mt: 2 }}>
            <Button variant="text" startIcon={<ArrowBackIcon />} onClick={() => navigate(location.state?.from || `${basePath}/laboratory/results`)} sx={{ mb: 1 }}>
                Back to Results
            </Button>
            {/* Order Header */}
            <Paper sx={{ p: 3, mb: 3 }} className="cardStyle">
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={3}>
                        <Typography variant="caption" color="text.secondary">Order</Typography>
                        <Typography variant="h6" fontWeight={600}>{order.labOrderId}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <Typography variant="caption" color="text.secondary">Patient</Typography>
                        <Typography>{order.patientId ? `${order.patientId.fName || ''} ${order.patientId.lName || ''}`.trim() : '-'}</Typography>
                        {order.patientId?.mrn && <Typography variant="caption" color="text.secondary">{order.patientId.mrn}</Typography>}
                    </Grid>
                    <Grid item xs={6} sm={2}>
                        <Typography variant="caption" color="text.secondary">Status</Typography>
                        <Box><Chip label={order.status} size="small" sx={{ textTransform: 'capitalize' }} /></Box>
                    </Grid>
                    <Grid item xs={6} sm={2}>
                        <Typography variant="caption" color="text.secondary">Priority</Typography>
                        <Typography sx={{ textTransform: 'capitalize' }}>{order.priority}</Typography>
                    </Grid>
                </Grid>
            </Paper>

            {/* GTT — consolidated timepoint widget (blood + urine per timepoint) */}
            {gttGroups.length > 0 && (
                <Paper sx={{ p: 3, mb: 3 }} className="cardStyle">
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6" fontWeight={600}>Glucose Tolerance Test (GTT)</Typography>
                        {gttPendingValidation.length > 0 && (
                            <Tooltip title={canValidate
                                ? 'Tech-validate all entered GTT timepoint results'
                                : 'You do not have validate access'}>
                                <span>
                                    <Button size="small" variant="outlined" disabled={!canValidate || resultLoading} onClick={handleValidateGtt}>
                                        Validate GTT ({gttPendingValidation.length})
                                    </Button>
                                </span>
                            </Tooltip>
                        )}
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                        Enter only the timepoints performed — blank timepoints are not saved or printed.
                    </Typography>
                    <TableContainer sx={{ mt: 1.5 }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 600, minWidth: 80 }}>Timepoint</TableCell>
                                    <TableCell sx={{ fontWeight: 600, minWidth: 150 }}>Blood Glucose (mg/dL)</TableCell>
                                    <TableCell sx={{ fontWeight: 600, minWidth: 150 }}>Urine Glucose</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Reference Range</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {gttGroups.map((g) => {
                                    const bId = g.blood?.id;
                                    const uId = g.urine?.id;
                                    const bResult = bId ? getResultForTest(bId) : null;
                                    return (
                                        <TableRow key={g.tp}>
                                            <TableCell>
                                                <Typography variant="body2" fontWeight={600}>{g.tp === 'FBS' ? 'GTT: FBS' : g.label}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                {g.blood ? (
                                                    <Stack direction="row" spacing={1} alignItems="center">
                                                        <TextField
                                                            size="small" type="number" placeholder="—" sx={{ width: 110 }}
                                                            value={values[bId] || ''}
                                                            onChange={(e) => setValues(prev => ({ ...prev, [bId]: e.target.value }))}
                                                            inputProps={{ step: 'any' }}
                                                        />
                                                        {bResult?.flag && bResult.flag !== 'N' && (
                                                            <Chip label={flagLabels[bResult.flag] || bResult.flag} size="small" color={flagColors[bResult.flag] || 'default'} />
                                                        )}
                                                    </Stack>
                                                ) : <Typography variant="body2" color="text.disabled">—</Typography>}
                                            </TableCell>
                                            <TableCell>
                                                {g.urine ? (
                                                    <FormControl size="small" sx={{ minWidth: 130 }}>
                                                        <Select
                                                            value={values[uId] || ''} displayEmpty
                                                            onChange={(e) => setValues(prev => ({ ...prev, [uId]: e.target.value }))}
                                                        >
                                                            <MenuItem value="">—</MenuItem>
                                                            {g.urine.test?.codedValues?.map(cv => <MenuItem key={cv} value={cv}>{cv}</MenuItem>)}
                                                        </Select>
                                                    </FormControl>
                                                ) : <Typography variant="body2" color="text.disabled">—</Typography>}
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="caption" color="text.secondary">{gttRangeStr(g.blood?.test)}</Typography>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    {gttGroups[0]?.blood && (
                        <TextField
                            size="small" fullWidth variant="standard" multiline sx={{ mt: 2 }}
                            placeholder="Report note (prints on report)…"
                            value={reportNotes[gttGroups[0].blood.id] || ''}
                            onChange={(e) => setReportNotes(prev => ({ ...prev, [gttGroups[0].blood.id]: e.target.value }))}
                            InputProps={{ startAdornment: <DescriptionIcon fontSize="small" sx={{ mr: 0.5, color: 'primary.light' }} /> }}
                        />
                    )}
                </Paper>
            )}

            {/* Result Entry Table */}
            {genericTests.length > 0 && (
            <Paper sx={{ p: 3, mb: 3 }} className="cardStyle">
                <Typography variant="h6" fontWeight={600} mb={2}>Test Results</Typography>
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 600, minWidth: 120 }}>Test</TableCell>
                                <TableCell sx={{ fontWeight: 600, minWidth: 180 }}>Result</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Unit</TableCell>
                                <TableCell sx={{ fontWeight: 600, minWidth: 160 }}>Ref Range</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Flag</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                                {/* Sticky so validate/authorize stay reachable when the row's
                                    inputs push the table wider than the screen (laptop widths
                                    at 100% zoom made users zoom out to find these buttons). */}
                                <TableCell sx={{ fontWeight: 600, position: 'sticky', right: 0, bgcolor: 'background.paper', borderLeft: 1, borderColor: 'divider' }} align="right">Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {genericTests.map((t, i) => {
                                const test = t.testId;
                                const testId = test?._id || t.testId;
                                const result = getResultForTest(testId);
                                const isNumeric = test?.resultType === 'numeric';
                                const isCoded = test?.resultType === 'coded';
                                const isCalculated = test?.resultType === 'calculated';

                                return (
                                    <TableRow key={i} sx={result?.isCritical ? { backgroundColor: 'error.light' } : {}}>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight={600}>{test?.testCode || '-'}</Typography>
                                            <Typography variant="caption" color="text.secondary">{test?.name || '-'}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            {isCalculated ? (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Typography variant="body2" fontWeight={500}>
                                                        {result?.numericValue !== null && result?.numericValue !== undefined
                                                            ? result.numericValue
                                                            : <Typography component="span" variant="body2" color="text.secondary" fontStyle="italic">Pending dependencies</Typography>
                                                        }
                                                    </Typography>
                                                    <Chip icon={<CalculateIcon />} label="Calculated" size="small" variant="outlined" color="info" />
                                                </Box>
                                            ) : isCoded ? (
                                                <FormControl size="small" fullWidth>
                                                    <Select
                                                        value={values[testId] || ''}
                                                        onChange={(e) => setValues(prev => ({ ...prev, [testId]: e.target.value }))}
                                                        displayEmpty
                                                    >
                                                        <MenuItem value="">Select...</MenuItem>
                                                        {test?.codedValues?.map(cv => (
                                                            <MenuItem key={cv} value={cv}>{cv}</MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            ) : (
                                                <TextField
                                                    size="small"
                                                    fullWidth
                                                    // Numeric fields use a text input (not type="number") so a tech can
                                                    // key comparator-qualified results like "<2.0" / ">1000" (e.g. BHCG);
                                                    // a number input blanks any non-numeric string and blocks "<"/">".
                                                    // inputMode="decimal" still gives a numeric keypad on mobile.
                                                    type="text"
                                                    inputMode={isNumeric ? 'decimal' : undefined}
                                                    multiline={test?.resultType === 'multiline'}
                                                    rows={test?.resultType === 'multiline' ? 2 : 1}
                                                    value={values[testId] || ''}
                                                    onChange={(e) => setValues(prev => ({ ...prev, [testId]: e.target.value }))}
                                                    placeholder={isNumeric ? 'e.g. 2.5 or <2.0' : 'Enter result'}
                                                />
                                            )}
                                            {/* Two notes per result: an internal technician note (lab-only,
                                                not printed) and a report note that prints on the patient
                                                report (saved as interpretiveNotes). */}
                                            {!isCalculated && (
                                                <>
                                                    <TextField
                                                        size="small" fullWidth variant="standard" multiline
                                                        placeholder="Internal note (not printed)…"
                                                        value={notes[testId] || ''}
                                                        onChange={(e) => setNotes(prev => ({ ...prev, [testId]: e.target.value }))}
                                                        InputProps={{ startAdornment: <EditNoteIcon fontSize="small" sx={{ mr: 0.5, color: 'text.disabled' }} /> }}
                                                        sx={{ mt: 0.5 }}
                                                    />
                                                    <TextField
                                                        size="small" fullWidth variant="standard" multiline
                                                        placeholder="Report note (prints on report)…"
                                                        value={reportNotes[testId] || ''}
                                                        onChange={(e) => setReportNotes(prev => ({ ...prev, [testId]: e.target.value }))}
                                                        InputProps={{ startAdornment: <DescriptionIcon fontSize="small" sx={{ mr: 0.5, color: 'primary.light' }} /> }}
                                                        sx={{ mt: 0.5 }}
                                                    />
                                                </>
                                            )}
                                        </TableCell>
                                        <TableCell>{test?.unit || '-'}</TableCell>
                                        <TableCell>
                                            {isNumeric && test?.referenceRanges?.length > 1 ? (() => {
                                                const sel = selectedRanges[testId] || '';
                                                const auto = matchRange(test.referenceRanges, patientAge, patientGender);
                                                return (
                                                    <FormControl size="small" fullWidth sx={{ minWidth: 150 }}>
                                                        <Select
                                                            value={sel}
                                                            onChange={(e) => setSelectedRanges(prev => ({ ...prev, [testId]: e.target.value }))}
                                                            displayEmpty
                                                            renderValue={(val) => {
                                                                const r = val ? test.referenceRanges.find(x => String(x._id) === String(val)) : auto;
                                                                if (!r) return <Typography variant="caption" color="text.secondary">Auto (age/sex)</Typography>;
                                                                return <Typography variant="caption" noWrap>{val ? '' : 'Auto · '}{rangeLabel(r)}: {formatRange(r)}</Typography>;
                                                            }}
                                                        >
                                                            <MenuItem value=""><em>Auto (age/sex)</em></MenuItem>
                                                            {test.referenceRanges.map(r => (
                                                                <MenuItem key={r._id} value={r._id}>{rangeLabel(r)}: {formatRange(r)}</MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                );
                                            })() : (
                                                <Typography variant="caption">{formatRange(test?.referenceRanges?.[0])}</Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {result?.flag && (
                                                <Chip
                                                    label={flagLabels[result.flag] || result.flag}
                                                    size="small"
                                                    color={flagColors[result.flag] || 'default'}
                                                />
                                            )}
                                            {result?.deltaCheckFlag && (
                                                <Chip label={`Delta ${result.deltaPercentage}%`} size="small" color="warning" sx={{ ml: 0.5 }} />
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {result && (
                                                <Chip
                                                    label={result.status?.replace(/_/g, ' ')}
                                                    size="small"
                                                    color={statusColors[result.status] || 'default'}
                                                    sx={{ textTransform: 'capitalize' }}
                                                />
                                            )}
                                        </TableCell>
                                        <TableCell align="right" sx={{ position: 'sticky', right: 0, bgcolor: result?.isCritical ? 'error.light' : 'background.paper', borderLeft: 1, borderColor: 'divider' }}>
                                            <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                                {order?.patientId?._id && result && (
                                                    <Tooltip title="Test history">
                                                        <IconButton size="small" onClick={() => handleShowHistory(testId, test?.name || test?.testCode || 'Test')}>
                                                            <HistoryIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                                {result?.isCritical && !result?.criticalNotifiedAt && (
                                                    <Tooltip title="Record critical notification">
                                                        <Button size="small" variant="text" color="error" startIcon={<NotificationsActiveIcon />} onClick={() => handleCriticalNotify(result)}>
                                                            Notify
                                                        </Button>
                                                    </Tooltip>
                                                )}
                                                {result?.isCritical && result?.criticalNotifiedAt && (
                                                    <Tooltip title={`Notified: ${result.criticalNotifiedTo} at ${new Date(result.criticalNotifiedAt).toLocaleString('en-GB')}`}>
                                                        <Chip label="Notified" size="small" color="success" variant="outlined" />
                                                    </Tooltip>
                                                )}
                                                {contactPending(result) && ['preliminary', 'tech_validated'].includes(result?.status) && (
                                                    <Tooltip title="Abnormal result — contact the ordering physician or patient before finalizing">
                                                        <Button size="small" variant="text" color="warning" startIcon={<PhoneInTalkIcon />} onClick={() => handleRecordContact(result)}>
                                                            Record Contact
                                                        </Button>
                                                    </Tooltip>
                                                )}
                                                {abnormalContactGate && isAbnormal(result) && result?.abnormalContactConfirmedAt && (
                                                    <Tooltip title={`${result.abnormalContactParty === 'patient' ? 'Patient' : 'Physician'} contacted: ${result.abnormalContactName} at ${new Date(result.abnormalContactConfirmedAt).toLocaleString('en-GB')}`}>
                                                        <Chip label="Contacted" size="small" color="success" variant="outlined" />
                                                    </Tooltip>
                                                )}
                                                {result?.status === 'preliminary' && (
                                                    <Tooltip title={canValidate ? 'Tech-validate this result' : 'You do not have validate access'}>
                                                        <span>
                                                            <Button size="small" variant="text" disabled={!canValidate} onClick={() => dispatch(techValidateLabResultRequest(result._id))}>
                                                                Validate
                                                            </Button>
                                                        </span>
                                                    </Tooltip>
                                                )}
                                                {/* Authorize is offered only once the result is tech-validated —
                                                    a preliminary result shows the Validate button above instead,
                                                    enforcing validate → authorize. (Backend also gates this.) */}
                                                {result?.status === 'tech_validated' && (
                                                    <Tooltip title={
                                                        !canAuthorize
                                                            ? 'You do not have authorize access'
                                                            : contactPending(result)
                                                                ? 'Locked: record physician/patient contact first'
                                                                : 'Finalize this result'
                                                    }>
                                                        <span>
                                                            <Button
                                                                size="small" variant="text" color="success"
                                                                disabled={!canAuthorize || contactPending(result)}
                                                                onClick={() => dispatch(authorizeLabResultRequest({ id: result._id }))}
                                                            >
                                                                Authorize
                                                            </Button>
                                                        </span>
                                                    </Tooltip>
                                                )}
                                                {['final', 'amended'].includes(result?.status) && (
                                                    <Tooltip title={canAuthorize ? 'Amend result' : 'You do not have authorize access'}>
                                                        <span>
                                                            <Button size="small" variant="text" color="secondary" disabled={!canAuthorize} startIcon={<EditNoteIcon />} onClick={() => handleAmend(result, test)}>
                                                                Amend
                                                            </Button>
                                                        </span>
                                                    </Tooltip>
                                                )}
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
            )}

            {/* Actions */}
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, justifyContent: { xs: 'center', sm: 'right' } }}>
                <Button className="commonBtn" variant="contained" onClick={handleSaveResults} disabled={resultLoading || !canEnter}>
                    {resultLoading ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}Save Results
                </Button>
                <Button variant="contained" color="success" onClick={handleBatchAuthorize} disabled={resultLoading || !canAuthorize}>Authorize All</Button>
                <Button variant="outlined" className="btn cancelBtn" onClick={() => navigate(location.state?.from || `${basePath}/laboratory/results`)}>Back</Button>
            </Box>

            {/* Critical Notification Dialog */}
            <Dialog open={criticalOpen} onClose={() => setCriticalOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Record Critical Value Notification</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 2 }} color="error">
                        Critical result for <strong>{criticalResult?.testId?.name || criticalResult?.testId?.testCode}</strong>
                    </Typography>
                    <TextField
                        fullWidth size="small" label="Notified To (name & role)" required sx={{ mb: 2, mt: 1 }}
                        value={notifiedTo} onChange={(e) => setNotifiedTo(e.target.value)}
                        placeholder="e.g. Dr. Smith, attending physician"
                    />
                    <FormControlLabel
                        control={<Checkbox checked={readBackConfirmed} onChange={(e) => setReadBackConfirmed(e.target.checked)} />}
                        label="Read-back confirmed"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCriticalOpen(false)}>Cancel</Button>
                    <Button variant="contained" color="error" onClick={confirmCriticalNotify} disabled={!notifiedTo.trim() || resultLoading}>
                        {resultLoading ? <CircularProgress size={20} /> : 'Record Notification'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Abnormal-result contact dialog (lis.abnormalContactGate) */}
            <Dialog open={contactOpen} onClose={() => setContactOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Record Abnormal Result Contact</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 2 }} color="warning.main">
                        Abnormal result for <strong>{contactResult?.testId?.name || contactResult?.testId?.testCode}</strong> — the ordering physician
                        or the patient must be contacted before this result can be finalized.
                    </Typography>
                    <FormControl sx={{ mb: 1 }}>
                        <RadioGroup row value={contactedParty} onChange={(e) => setContactedParty(e.target.value)}>
                            <FormControlLabel value="physician" control={<Radio size="small" />} label="Ordering physician" />
                            <FormControlLabel value="patient" control={<Radio size="small" />} label="Patient" />
                        </RadioGroup>
                    </FormControl>
                    <TextField
                        fullWidth size="small" label="Name of person contacted" required sx={{ mb: 2 }}
                        value={contactedName} onChange={(e) => setContactedName(e.target.value)}
                        placeholder="e.g. Dr. Smith"
                    />
                    <TextField
                        fullWidth size="small" label="Notes (optional)" multiline rows={2}
                        value={contactNotes} onChange={(e) => setContactNotes(e.target.value)}
                        placeholder="e.g. Spoke by phone, repeat test advised"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setContactOpen(false)}>Cancel</Button>
                    <Button variant="contained" color="warning" onClick={confirmRecordContact} disabled={!contactedName.trim() || resultLoading}>
                        {resultLoading ? <CircularProgress size={20} /> : 'Record Contact'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Amend Result Dialog */}
            <Dialog open={amendOpen} onClose={() => setAmendOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Amend Result</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 2 }} color="text.secondary">
                        Amend <strong>{amendResult?.test?.name || amendResult?.test?.testCode}</strong>
                        {amendResult?.numericValue !== undefined && ` (current: ${amendResult.numericValue})`}
                        {amendResult?.textValue && ` (current: ${amendResult.textValue})`}
                        {amendResult?.codedValue && ` (current: ${amendResult.codedValue})`}
                    </Typography>
                    {amendResult?.test?.resultType === 'coded' ? (
                        <FormControl fullWidth size="small" sx={{ mt: 1, mb: 2 }}>
                            <Select value={amendValue} onChange={(e) => setAmendValue(e.target.value)} displayEmpty>
                                <MenuItem value="">Select...</MenuItem>
                                {amendResult?.test?.codedValues?.map(cv => (
                                    <MenuItem key={cv} value={cv}>{cv}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    ) : (
                        <TextField
                            fullWidth size="small" label="New Value" sx={{ mt: 1, mb: 2 }}
                            // Text input (not type="number") so a comparator amendment like
                            // "<2.0" / ">1000" is typeable; inputMode keeps a numeric keypad.
                            type="text"
                            inputMode={['numeric', 'calculated'].includes(amendResult?.test?.resultType) ? 'decimal' : undefined}
                            multiline={amendResult?.test?.resultType === 'multiline'}
                            rows={amendResult?.test?.resultType === 'multiline' ? 2 : 1}
                            value={amendValue} onChange={(e) => setAmendValue(e.target.value)}
                        />
                    )}
                    <TextField
                        fullWidth size="small" label="Reason for Amendment" required multiline rows={2}
                        value={amendReason} onChange={(e) => setAmendReason(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAmendOpen(false)}>Cancel</Button>
                    <Button variant="contained" color="secondary" onClick={confirmAmend} disabled={!amendReason.trim() || resultLoading}>
                        {resultLoading ? <CircularProgress size={20} /> : 'Amend'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Test History Dialog */}
            <Dialog open={historyOpen} onClose={() => { setHistoryOpen(false); dispatch(resetGetLabResultTestHistory()); }} maxWidth="sm" fullWidth>
                <DialogTitle>Test History - {historyTestName}</DialogTitle>
                <DialogContent>
                    {resultLoading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress /></Box>}
                    {!resultLoading && getTestHistoryRes?.data?.length > 0 ? (
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Value</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Flag</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {getTestHistoryRes.data.map((h, i) => (
                                        <TableRow key={i}>
                                            <TableCell>{h.enteredAt ? new Date(h.enteredAt).toLocaleDateString('en-GB') : '-'}</TableCell>
                                            <TableCell>
                                                {h.numericValue ?? h.textValue ?? h.codedValue ?? h.multilineValue ?? '-'}
                                                {h.unit ? ` ${h.unit}` : ''}
                                            </TableCell>
                                            <TableCell>
                                                {h.flag && <Chip label={flagLabels[h.flag] || h.flag} size="small" color={flagColors[h.flag] || 'default'} />}
                                            </TableCell>
                                            <TableCell>
                                                <Chip label={h.status?.replace(/_/g, ' ')} size="small" color={statusColors[h.status] || 'default'} sx={{ textTransform: 'capitalize' }} />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    ) : !resultLoading ? (
                        <Typography color="text.secondary" sx={{ mt: 1 }}>No previous results found for this test.</Typography>
                    ) : null}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setHistoryOpen(false); dispatch(resetGetLabResultTestHistory()); }}>Close</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
